/**
 * 노출 대상(audience) 선택 후보 — **두 역할 체계의 행을 API로 받아** `<네임스페이스>:<역할>` 참조로 만든다.
 *
 * 규약·판정의 정본은 `packages/shared/src/features/audience-targeting.ts`이고 결정은
 * `docs/decisions/2026-10-19-audience-targeting-is-a-namespaced-role-set.md`다.
 *
 * ## ⚠️ 역할 key를 여기 열거하지 «않는다» — 둘 다 DB 행이다
 * 조직 역할은 `company_roles`(조직 커스텀·상속 가능), 슈퍼관리자 역할은 `super_admin_roles`
 * (커스텀 가능)다. 결정 §3이 「역할 key를 코드에 열거하지 말 것 — 행이 추가될 때 조용히 낡는다」라
 * 적는 자리가 바로 여기다. 그래서 후보는 **두 조회 API**에서 온다:
 *  - 조직 역할: `/admin/rbac/roles` — 기존 훅 {@link useSystemRoleOptions}를 그대로 쓴다
 *    (보관된 역할을 이미 걸러 준다)
 *  - 슈퍼관리자 역할: `/admin/roles` — `adminApi.getRoles()`
 *
 * ## ⚠️ 접두는 **이 모듈이 붙인다** — 사람이 타이핑하지 않는다
 * `buildAudienceRef`(shared)로 만든다. 손으로 `'company:' + key`를 이어 붙이지 말 것 — 구분자가
 * 바뀌는 날(그럴 일은 거의 없지만) 두 자리가 갈린다. 저작 화면은 **이 `ref` 값을 그대로** 서버로
 * 보낸다(서버 DTO가 `isAudienceRef`로 다시 본다 — 입구 거부가 정본 처방이고 여기 UI는 **오타를
 * 만들지 않는 수단**이다).
 *
 * ## ⚠️ 권한 축이 저작 화면과 **다르다** — 그래서 실패를 가린다
 * 두 조회 API의 가드가 각각 `@RequirePermission('system-settings')`·`'admin-management'`다.
 *
 * ## 🔴 2026-10-21 — **전제가 바뀠다. 비대칭이 좁아졌고 없어지지는 않았다**
 * 결정 채택 ④(`docs/decisions/2026-10-21-release-note-surfaces-split-anonymous-visibility-from-consumer-reach.md`)로
 * **관리 뷰가 `system-settings` 뒤로 들어갔다** — 서버 `AdminReleaseNotesController`가 클래스 레벨
 * `@RequirePermission('system-settings')`를 들고 `Layout.tsx`의 `/release-notes` 행도 같은 키를 든다.
 * ⇒ **이 피커가 뜨는 화면에 닿는 사람은 `system-settings`를 반드시 가지므로** 조직 체계 후보
 * (`/admin/rbac/roles`)는 **언제나 열린다.** 남는 비대칭은 **`admin-management`를 요구하는 sa 역할
 * 후보(`/admin/roles`) 하나**이고, 그 권한이 없으면 그 체계만 403이다(아래 「두 체계 중 하나만
 * 실패할 수 있다」가 정확히 그 자리다).
 *
 * ⚠️ **「원래 그래야 했다」로 읽지 말 것** — 2026-10-21 이전 상태(권한 요구 없음)는 **그때 서버와
 * 일치했으므로 옳았다.** 바뀐 것은 서버의 게이트이고, 그래서 이 문단은 「틀린 것을 고쳤다」가
 * 아니라 **「전제가 바뀌어 뒤집혔다」**다. 종전 서술을 경위로 남긴다:
 * ~~업데이트 내역 화면에는 권한 요구가 아예 없다 … 그 화면은 슈퍼관리자 전원에게 열려 있다.
 * `/release-notes`에 `permissionKey`를 달지 말 것 — 서버와 일치하는 것이 맞다.~~
 * (그 서술에는 **2026-10-21 오전의 정정**이 함께 붙어 있었다: 그 전에는 「그 화면의 권한은
 * `release-notes`다」라 적고 있었고 그것은 **거짓**이었다 — 문자열 `'release-notes'`가 사는 자리는
 * `Layout.tsx`의 `pathQueryKeys` 하나이고 그것은 **react-query 캐시 키**다. 두 정정의 성질이
 * 다르니 섞어 읽지 말 것: 앞은 **없는 사실**을 지웠고 이것은 **참이던 사실의 전제**가 바뀐 것이다.)
 *
 * ⚠️ 수퍼관리자가 **자기에게 보이는** 내역을 읽는 자리는 이제 **다른 화면**이다
 * (`/release-note-feed` — 권한 요구 없음·읽기만). 그 화면은 이 피커를 **쓰지 않는다**(노출 대상은
 * 관리 축이다) ⇒ 「전원에게 열린 화면이 있으니 비대칭이 그대로다」로 읽지 말 것.
 *
 * SYSTEM_ADMIN은 `@RequirePermission`을 자동 통과하지만 **커스텀 슈퍼관리자 역할은 403이다** —
 * 가능성이 아니라 **실측**이다(아래 표). 그때 `error`가 참이 되고 **호출부는 피커를 비활성**으로
 * 두어야 한다 — 자유 타이핑으로 열면 접두·역할 오타가 들어와 「아무에게도 안 보이는」 노트가
 * 생기고 그 실패는 조용하다(결정 §5).
 *
 * ### 전용 경량 엔드포인트를 만들지 않았다 — 되짚을 조건
 * 「`release-notes` 권한만으로 역할 목록을 받는 엔드포인트」를 만들 수 있지만, 권한 축을 하나 더
 * 만드는 비용이 **실측 수요보다 크다**(2026-10-19 팀장 확정). 그 판단의 재료를 수치로 남긴다 —
 * 조건·재료·수치가 함께 없으면 그 결정은 **미룬 것이 아니라 없앤 것**이 된다:
 *
 * | 재료 | 2026-10-19 prod 실측 |
 * |---|---|
 * | 커스텀 sa 역할(`super_admin_roles WHERE NOT is_system`) | **3** |
 * | 그 역할을 보유한 계정 | **1** |
 * | 그 역할 중 `admin-management` 보유 | **0** |
 * | 그 역할 중 `system-settings` 보유 | **0** |
 *
 * ⇒ 그 1계정은 피커가 비활성이어도 **저작 자체는 가능하다**(비우면 「모두에게」이고 그것이 기본값).
 *
 * ⚠️ **2026-10-21 이후 그 표를 다시 읽으면 결론이 한 겹 더 강해진다** — 관리 뷰가
 * `system-settings` 뒤로 들어갔고 그 표의 마지막 행이 **0**이므로, **오늘 prod 구성에서 그 화면에
 * 닿는 것은 `SYSTEM_ADMIN`뿐이고 그 역할은 두 권한을 자동 통과한다** ⇒ 「피커 비활성」은 **지금
 * 도달 불가**다. 세 갈래를 가려 적는다: ① 시드·기본 구성에 그 인물이 **없다** ② 조직이 커스텀
 * sa 역할에 `system-settings`를 주면 **생긴다**(그때 `admin-management`가 없으면 sa 체계만 막힌다)
 * ③ 구현상 불가능한 것은 **아니다**. ⇒ **그래서 비활성 처리를 지우지 말 것** — 그것은 ②를 막는
 * 심층 방어이고, 「오늘 발동 0」은 **빈도**이지 **불가능**이 아니다.
 *
 * **되짚을 조건**: 커스텀 sa 역할이 **`system-settings`를 얻거나**(그 순간 ②가 실재가 된다) 그
 * 보유 계정이 늘거나, 누군가 **피커 비활성을 보고하면** 전용 경량 엔드포인트를 판단한다.
 * **되짚을 쿼리**(대조군을 같은 쿼리에 둔다 — 축이 틀리면 0이 나온다):
 * ```sql
 * SELECT count(*) FILTER (WHERE NOT r.is_system)            AS custom_roles,   -- 기대 3
 *        count(DISTINCT a.id) FILTER (WHERE NOT r.is_system) AS custom_holders, -- 기대 1
 *        count(*)                                            AS all_roles       -- [대조군] >0
 * FROM super_admin_roles r LEFT JOIN super_admins a ON a.role_id = r.id AND a.deleted_at IS NULL;
 * ```
 *
 * ⚠️ **두 체계 중 하나만 실패할 수 있다** — 그때 「아무것도 못 고른다」로 떨어뜨리지 않는다(된 쪽은
 * 고를 수 있어야 한다). 대신 **어느 체계가 막혔는지**를 호출부가 말할 수 있게 체계별로 돌려준다.
 *
 * ## 공지·팝업도 같은 후보를 쓴다
 * 노출 대상 축은 세 surface가 **같은 두 체계**를 쓰므로(결정 §2) 이 훅이 그 셋의 공용이어야 한다.
 * 2026-10-19에 세 작업이 **병렬**이라 각자 자기 화면을 만들고 있다 ⇒ merge 뒤에 중복이 있으면
 * 이쪽으로 합칠 것(후보 목록이 갈리면 「공지에서는 고를 수 있는데 노트에서는 못 고르는」 역할이
 * 생기고, 그 갈라짐에는 red가 없다).
 */
import { useQuery } from '@tanstack/react-query';
import {
  AUDIENCE_NAMESPACES,
  AUDIENCE_NAMESPACE_LABELS,
  AUDIENCE_NAMESPACE_VALUES,
  buildAudienceRef,
  parseAudienceRef,
  type AudienceNamespace,
  type AudienceRef,
} from '@safetyhub/shared';
import { adminApi } from './api';
import { companyRbacApi } from './company-rbac';
import { useSystemRoleOptions } from './roles';

/**
 * 역할 체계 이름 — 화면 라벨. 「네임스페이스」는 개발 어휘라 운영자에게 보이지 않는다.
 *
 * ⚠️ **2026-10-20 — 정본이 shared로 올라갔다.** 종전엔 이 파일이 값을 직접 들고 `as const`였는데,
 * **서버도 같은 라벨이 필요해졌다**(저작 거부 문구가 체계를 이름 들어 말하는데 그 문구를 서버가
 * 만든다 — `announcement-authoring.service.ts`·`release-notes.service.ts`). admin에만 두면 서버가
 * 고칠 수 없고, admin이 서버 문구를 파싱해 번역하면 **문구 축의 소유자가 둘이 된다**(그 처방은
 * 리뷰에서 명시로 기각됐다).
 *
 * ⇒ 여기서는 **재export만** 한다. 두 벌로 두면 「공지 오류 문구의 체계 이름」과 「피커의 묶음
 * 이름」이 갈리는데 **그 갈라짐에는 red가 없다**(둘 다 유효한 문자열이다). shared 쪽은
 * `Record<AudienceNamespace, string>`이라 체계가 늘면 **tsc가 그 자리에서** 깨진다 — 종전 이 파일의
 * `as const`는 그것을 **못 막았다**(리뷰 항목: 「세 번째 체계가 생기면 피커에 안 뜨고 경고도 영영
 * 발동하지 않는데 tsc조차 조용하다」).
 */
export { AUDIENCE_NAMESPACE_LABELS };

export interface AudienceOption {
  /** 서버로 보내는 값 — `<네임스페이스>:<역할>` */
  ref: AudienceRef;
  /** 화면 라벨(역할 이름). 운영자는 key가 아니라 이 이름으로 고른다 */
  label: string;
  /** 같은 체계의 후보를 묶어 보이기 위한 구분 */
  namespace: (typeof AUDIENCE_NAMESPACES)[keyof typeof AUDIENCE_NAMESPACES];
}

export interface AudienceOptionGroup {
  namespace: AudienceOption['namespace'];
  /** 체계 이름 — `AUDIENCE_NAMESPACE_LABELS` */
  label: string;
  options: AudienceOption[];
  /** 그 체계의 목록을 못 받았다(대개 권한 403) — 호출부는 이 체계의 선택을 **잠근다** */
  failed: boolean;
}

/**
 * 두 체계의 후보를 체계별로 돌려준다.
 *
 * @param enabled 모달이 닫혀 있을 때 조회하지 않게. 두 조회가 **다른 권한**을 요구하므로 화면을
 *   열지도 않고 403을 받아 둘 필요가 없다.
 */
export function useAudienceOptions(enabled = true): {
  groups: AudienceOptionGroup[];
  isLoading: boolean;
  /** 한 체계라도 막혔나 — 호출부가 안내 문구를 띄우는 조건 */
  hasFailure: boolean;
} {
  // 조직 역할 — 기존 훅이 **후보**를 만든다(보관 역할 필터를 그쪽이 소유한다).
  // ⚠️ 그런데 그 훅은 실패를 돌려주지 않는다(`{ options, isLoading }`뿐) ⇒ 「0건」과 「403」이
  //    **같은 모양**이 된다. 그래서 **같은 쿼리 키·같은 queryFn**으로 한 번 더 구독해 에러만 읽는다
  //    (react-query는 키로 중복을 접으므로 **요청은 한 번**이다). 훅 자체를 고치면 그쪽 호출부
  //    셋(강제 지정·복원·생성 폼)의 동작을 함께 바꾸게 되므로 여기서 읽는 쪽이 좁다.
  const company = useSystemRoleOptions(enabled);
  const companyProbe = useQuery({
    queryKey: ['admin', 'rbac', 'roles', 'options'],
    queryFn: () => companyRbacApi.listRoles(),
    enabled,
    staleTime: 60_000,
  });

  const superAdmin = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => adminApi.getRoles(),
    enabled,
    staleTime: 60_000,
  });

  const companyOptions: AudienceOption[] = company.options.map((r) => ({
    ref: buildAudienceRef(AUDIENCE_NAMESPACES.company, r.key),
    label: r.name,
    namespace: AUDIENCE_NAMESPACES.company,
  }));

  const superAdminOptions: AudienceOption[] = (superAdmin.data?.data ?? []).map((r) => ({
    // sa 체계의 식별자는 `code`다(`super_admin_roles.code` — 조직 역할의 `key`와 **다른 칼럼**이다).
    ref: buildAudienceRef(AUDIENCE_NAMESPACES.superAdmin, r.code),
    label: r.name,
    namespace: AUDIENCE_NAMESPACES.superAdmin,
  }));

  /**
   * 체계별 후보·실패 — **`Record<AudienceNamespace, …>`로 두는 것이 이 자리의 계약이다.**
   *
   * ⚠️ 종전에는 아래 `groups`를 **배열로 손열거**했고, 그래서 세 번째 체계가 생기면 **피커에 안 뜨고
   * 「한쪽만 좁혔다」 경고도 영영 발동하지 않는데 tsc조차 조용했다**(서버는 `namespaceOfPlatform`의
   * `never` 대입이 막아 **비대칭**이었다 — 리뷰 2026-10-20). 저작자는 「고를 수 있는 것을 다 골랐다」고
   * 믿고 그 체계 전원이 대상이 된다.
   *
   * ⇒ 키를 `Record`로 두면 체계가 늘 때 **여기서 tsc가 깨진다.** 두 체계의 **출처가 다르므로**
   * (조직 역할은 `/admin/rbac/roles`, 슈퍼관리자 역할은 `/admin/roles`) 값 자체는 파생시킬 수 없다 —
   * 파생되는 것은 **「무엇을 빠뜨렸나」**이고 그것이 이 타입의 일이다.
   */
  const byNamespace: Record<AudienceNamespace, Pick<AudienceOptionGroup, 'options' | 'failed'>> = {
    [AUDIENCE_NAMESPACES.company]: {
      options: companyOptions,
      // ⚠️ 「후보 0건」을 실패로 세지 «않는다» — 그것이 바로 위 주석이 막으려는 혼동이다.
      //    목록이 정말 빌 수도 있고(그때 화면은 「후보 없음」이라 말한다), 403은 `error`로만 온다.
      failed: Boolean(companyProbe.error),
    },
    [AUDIENCE_NAMESPACES.superAdmin]: {
      options: superAdminOptions,
      failed: Boolean(superAdmin.error),
    },
  };

  // 순서도 **상수에서 파생**시킨다 — 손열거하면 체계를 더한 사람이 이 배열을 찾아야 한다
  const groups: AudienceOptionGroup[] = AUDIENCE_NAMESPACE_VALUES.map((namespace) => ({
    namespace,
    label: AUDIENCE_NAMESPACE_LABELS[namespace],
    ...byNamespace[namespace],
  }));

  return {
    groups,
    isLoading: company.isLoading || superAdmin.isLoading,
    hasFailure: groups.some((g) => g.failed),
  };
}

/**
 * 저장된 `audiences` 값을 화면 라벨로 — 후보에 없는 참조도 **가리지 않고 그대로 보여 준다**.
 *
 * ⚠️ 모르는 참조를 숨기지 말 것. 조직 커스텀 역할(`co-<cuid>`)·보관된 역할·다른 환경에서 들어온
 * 값이 그 모양으로 오고, 숨기면 운영자는 **대상이 비어 있다고 믿는다**(그런데 서버는 그 제약으로
 * 거른다). 같은 판단의 선례는 이 화면의 `platforms` 빈 배열 처리(`(없음)`을 빨갛게 보인다)다.
 */
export function audienceLabel(ref: string, groups: AudienceOptionGroup[]): string {
  for (const g of groups) {
    const hit = g.options.find((o) => o.ref === ref);
    if (hit) return `${g.label} · ${hit.label}`;
  }
  return ref;
}

/** 후보 목록에 없는 저장값을 **뜻이 다른 두 통**으로 가른 결과 — {@link splitUnlistedAudiences} */
export interface UnlistedAudiences {
  /**
   * 그 체계의 목록은 **받았는데** 거기 없다 — 보관된 역할·조직 커스텀 역할(`co-<cuid>`)·다른
   * 환경에서 들어온 값·접두가 무효한 값. 다시 고를 후보가 없으니 **제거가 유일한 처분**이다.
   */
  missing: string[];
  /**
   * 그 체계의 목록을 **못 받았다**(대개 403) — 그래서 후보에 없어 보이는 것이고, 값 자체는
   * **정상 제약일 수 있다**. 호출부는 이것을 **제거 불가**로 그린다.
   */
  unloadable: string[];
}

/**
 * 후보 목록에 없는 저장값을 **둘로 가른다** — 「없어진 역할」과 「못 받은 체계의 역할」은 다른 것이다.
 *
 * ## ⚠️ 왜 가르나 — 한 통에 담으면 운영자가 멀쩡한 제약을 지운다
 * 2026-10-21까지 **세 자리**(공지 저작 `AudienceSection` · 노출 대상 모달 **두 번**, 변수 없이 인라인)
 * 가 각자 `!groups.some((g) => g.options.some(…))` 하나로 걸러 **한 통**에 담고 「후보에 없는
 * 대상(보관·조직 전용 역할)」이라 이름 붙였다. 세 자리 모두 {@link AudienceOptionGroup.failed}를
 * 보지 않았다.
 *
 * 그런데 역할 목록 조회가 403이면 그 체계의 `options`가 **빈 배열**이 되므로, **그 체계의 정상
 * 저장값 전부**가 그 통으로 떨어진다. 그 통에서 할 수 있는 동작은 **✕(제거) 하나**이고 `title`이
 * 「클릭하면 제거합니다」라 ⇒ 운영자는 「없어진 역할이구나」로 읽고 **멀쩡한 노출 제약을 지운다 ⇒
 * 노출이 넓어진다.** 위험 방향이 나쁜 쪽이다(좁아지는 것이 아니라 넓어진다).
 *
 * 그 상태가 **구조적으로** 일어날 수 있다는 것은 가능성이 아니라 실측이다 — 위 docblock의 그 표가
 * 「커스텀 sa 역할 보유 계정 **1** · 그중 `admin-management` **0** · `system-settings` **0**」이라
 * 적는다(= 그 계정은 **두 조회 다 403**이다).
 *
 * ⇒ 체계를 shared `parseAudienceRef`로 찾아 **그 체계가 `failed`면 `unloadable`**로 가른다. 접두를
 * 여기서 `split`하지 말 것(구분자의 소유자는 shared다). 접두가 무효한 값은 체계를 찾을 수 없으니
 * `missing`이다 — 어느 체계의 목록을 받아도 후보로 돌아오지 않으므로 처분이 같다.
 *
 * ## ⚠️ 이 파일에 둔 이유 — 파생값을 호출부 로컬로 뽑으면 두 벌이 남는다
 * 같은 식이 **파일 둘**에 걸쳐 세 벌이었다. 위 docblock이 「공지·팝업도 같은 후보를 쓴다 … 중복이
 * 있으면 이쪽으로 합칠 것」이라 적는 자리가 바로 여기다. 호출부마다 파생값을 두면 한 화면만 고치고
 * 다른 화면은 옛 판정을 남기는데, **그 갈라짐에는 red가 없다**(둘 다 유효한 배열이다).
 *
 * @param selected 지금 선택·저장된 `audiences` 값
 * @param groups {@link useAudienceOptions}가 돌려준 체계별 후보. `failed`가 이 함수의 **입력**이다
 */
export function splitUnlistedAudiences(
  selected: readonly string[],
  groups: readonly AudienceOptionGroup[],
): UnlistedAudiences {
  const missing: string[] = [];
  const unloadable: string[] = [];

  for (const ref of selected) {
    if (groups.some((g) => g.options.some((o) => o.ref === ref))) continue;
    const parsed = parseAudienceRef(ref);
    const group = parsed ? groups.find((g) => g.namespace === parsed.namespace) : undefined;
    if (group?.failed) unloadable.push(ref);
    else missing.push(ref);
  }

  return { missing, unloadable };
}
